<script setup>
import { LockClosedIcon, RefreshIcon, CheckIcon, CheckCircleIcon, XIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { computed, onMounted, ref } from 'vue'
import BaseInputText from '../../base/BaseInputText.vue'
import { storeToRefs } from 'pinia'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { useNyalaCustomerVaultData } from '../../../utilities/account/useNyalaCustomerVaultData'
import { checkNyalaPin, resetNyalaPin } from '../../../utilities/api/nyala-customer'
import { notify } from '../../../utilities/useNotifications'
import { i18n } from '../../../localization'
import { openAlertDialog, openConfirmDialog } from '../../../utilities/useDialog'
import AlertDialog from '../../dialogs/AlertDialog.vue'

const pin = storeToRefs(useInvestmentProcessDataState()).nyalaPassphrase
const nyalaCustomerVaultData = useNyalaCustomerVaultData()
const { id } = storeToRefs(nyalaCustomerVaultData)

const nyalaPinExists = computed(() => {
    return id.value !== null
})

const pinConfirm = ref('')
const pinConfirmed = computed(() => {
    return pinConfirm.value === pin.value
})

const pinInvalid = ref(false)

async function confirmPin() {
    const response = await checkNyalaPin(id.value, pinConfirm.value)
    if (response.data.pinValid === true) {
        notify('Success', i18n.global.t('nyala_vault.pin_confirmed'))
        pin.value = pinConfirm.value
        pinInvalid.value = false
    } else {
        pinConfirm.value = ''
        pinInvalid.value = true
        notify('Error', i18n.global.t('nyala_vault.invalid_nyala_pin'))
    }
}

async function resetPin() {
    if (useNyalaCustomerVaultData().isValidPin(pinConfirm.value) === false) {
        await openAlertDialog(
            i18n.global.t('nyala_vault.invalid_pin'),
            i18n.global.t('nyala_vault.enter_a_valid_pin'),
            {
                type: 'Error',
                hideCancel: true,
            }
        )
        pinConfirm.value = ''
        return
    }

    await resetNyalaPin(useNyalaCustomerVaultData().id, pinConfirm.value)
    await openConfirmDialog(i18n.global.t('nyala_vault.reset_initiated_info'), '', {
        type: 'Success',
        hideCancel: true,
    })
    pinConfirm.value = ''
}

async function generateRandomPin() {
    pin.value = await useNyalaCustomerVaultData().generateRandomPin()
}
</script>

<template>
    <AlertDialog />
    <div class="mt-4 overflow-hidden rounded-lg border">
        <div class="px-5 py-3">
            <h3 class="flex text-lg font-bold text-gray-900">
                <LockClosedIcon class="mr-2 w-5 text-gray-600" />
                {{ $t('investment_process.nyala_vault') }}
            </h3>
            <div v-if="nyalaPinExists">
                <p class="mt-2 font-medium text-gray-500">
                    {{ $t('nyala_vault.nyala_confirm_pin') }}
                </p>
                <div class="flex flex-col space-y-4 pt-4 md:flex-row md:space-x-4">
                    <div class="w-full md:w-1/2">
                        <BaseInputText
                            v-model="pinConfirm"
                            maxlength="6"
                            :disabled="pinConfirmed"
                            name="pin"
                            label="PIN-Code"></BaseInputText>
                    </div>
                    <p class="w-full text-sm font-medium text-gray-500 md:w-1/2">
                        {{ $t('nyala_vault.enter_confirm_pin') }}
                    </p>
                </div>
                <div v-if="pinConfirmed === false" class="w-full space-y-2 pt-2">
                    <div v-if="pinInvalid === true" class="flex w-full items-center space-x-1.5 pb-2 text-red-600">
                        <XIcon class="w-5"></XIcon>
                        <p class="text-sm font-medium">
                            {{ $t('nyala_vault.invalid_nyala_pin') }}
                        </p>
                    </div>
                    <BaseButtonMuted class="w-full md:w-auto" @click="confirmPin">
                        <CheckIcon class="mr-2 w-5"></CheckIcon>
                        {{ $t('nyala_vault.confirm_pin') }}
                    </BaseButtonMuted>
                    <BaseButtonMuted class="w-full md:ml-2 md:w-auto" @click="resetPin">
                        <XIcon class="mr-2 w-5 text-red-500"></XIcon>
                        {{ $t('nyala_vault.reset_pin') }}
                    </BaseButtonMuted>
                </div>
                <div v-else class="flex items-center space-x-1.5 py-2 text-green-600">
                    <CheckCircleIcon class="w-5"></CheckCircleIcon>
                    <p class="text-base font-medium">
                        {{ $t('nyala_vault.pin_confirmed') }}
                    </p>
                </div>
            </div>
            <div v-else>
                <p class="mt-2 text-sm font-medium text-gray-500">
                    {{ $t('nyala_vault.nyala_new_pin') }}
                </p>
                <div class="flex flex-col space-y-4 pt-4 md:flex-row md:space-x-4">
                    <div class="w-full md:w-1/2">
                        <BaseInputText v-model="pin" maxlength="6" name="pin" label="PIN-Code"></BaseInputText>
                        <BaseButtonMuted class="mt-2 w-full" @click="generateRandomPin()">
                            <RefreshIcon class="mr-2 w-5"></RefreshIcon>
                            {{ $t('nyala_vault.generate_random_pin') }}
                        </BaseButtonMuted>
                    </div>
                    <div
                        class="flex w-full items-center rounded-xl border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 md:w-1/2">
                        <p>{{ $t('nyala_vault.pin_info') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
